import Phaser from 'phaser';
import type {
  GameMode,
  GameStartPayload,
  LobbyDisbandedPayload,
  MatchFoundPayload,
  MultiplayerLobbyState,
} from '@roe/shared';
import { GAME_W, GAME_H, setupCamera } from '../../dpr.js';
import { MultiplayerService } from '../../services/MultiplayerService.js';
import { GameStateManager } from '../../state/GameStateManager.js';
import { AuthService } from '../../services/AuthService.js';
import { buildSaveSlotCard, type SaveSlotCardHandle } from '../../ui/SaveSlotCard.js';
import { InviteToast } from '../../ui/InviteToast.js';
import { SocialHub } from '../../ui/SocialHub.js';
import { attachPendingBadge, attachFriendOnlineToasts } from '../../ui/SocialNotifications.js';

export type MultiplayerLobbyState4 = 'idle' | 'queuing' | 'matched' | 'launching';

/**
 * Abstract base for the per-mode multiplayer lobby scenes (PvpLobbyScene,
 * CoopLobbyScene). Owns the 4-state machine (IDLE/QUEUING/MATCHED/LAUNCHING),
 * socket plumbing, and shared chrome (header, save picker container, leave
 * button, status banner).
 *
 * State transitions (server-authoritative — client only renders / drives input):
 *   IDLE      ──[Join Queue clicked]──▶  QUEUING
 *   QUEUING   ──[Cancel Queue clicked]─▶ IDLE
 *   QUEUING   ──[match-found]─────────▶  MATCHED
 *   MATCHED   ──[lobby-state-update phase=countdown]──▶ LAUNCHING
 *   MATCHED   ──[lobby-disbanded reason=ready-timeout-self|both-timeout]──▶ IDLE
 *   MATCHED   ──[lobby-disbanded reason=ready-timeout-other|opponent-disconnect|player-left]──▶ QUEUING
 *   MATCHED   ──[Leave Lobby clicked]──▶ IDLE
 *   LAUNCHING ──[lobby-state-update phase=hero_select]──▶ MATCHED (un-ready)
 *   LAUNCHING ──[lobby-disbanded *]────▶  routes per reason (same as MATCHED)
 *   LAUNCHING ──[game-start]───────────▶ BattleScene (scene change)
 *
 * Subclasses implement: mode getter, theme color, mode label, save picker
 * rendering (mode-specific), and queue payload shape.
 */
export abstract class MultiplayerLobbyBase extends Phaser.Scene {
  /** Public-readable state for tests; mutated only via _transition(). */
  protected state: MultiplayerLobbyState4 = 'idle';

  /** roomId set when match-found arrives; cleared on transition back to IDLE. */
  protected roomId = '';
  protected opponentId = '';
  protected playerIndex: 1 | 2 = 1;

  /** Mode-specific overrides — concrete subclasses MUST implement these. */
  protected abstract readonly mode: GameMode;
  protected abstract getThemeColor(): number;
  protected abstract getModeLabel(): string;
  /** Key of the preloaded background image to render behind the chrome. Mode
   *  subclasses return e.g. 'zone_background_rift' (PVP) / 'zone_background_forest' (Coop).
   *  If the texture isn't available, the scene falls back to the solid bg color. */
  protected abstract getBackgroundKey(): string;
  /** Build the join-queue payload (mode-specific data). Returns undefined to
   *  signal "no save selected — Join Queue should not have been clickable". */
  protected abstract getQueuePayload(): { mode: GameMode; [k: string]: unknown } | undefined;

  /** Save slot selected on the main menu and passed into this scene via init.
   *  Used by `renderIdleSaveCard` and the concrete subclasses' `getQueuePayload`. */
  public selectedSaveSlot?: number;

  /** Left-side save card (the player's own). Rebuilt on every IDLE render. */
  private leftCard?: SaveSlotCardHandle;

  /** Right-side save card (opponent's). Rendered on match-found in Task 4. */
  private rightCard?: SaveSlotCardHandle;

  protected mpService = MultiplayerService.getInstance();

  /** Server-authoritative ready window (mirrors `READY_TIMEOUT_MS` in
   *  packages/server/src/multiplayer/lobby.ts). Used by the matched-state
   *  client-local countdown so the displayed seconds match what the server
   *  is actually counting down. */
  private static readonly READY_TIMEOUT_MS = 60_000;

  /** Timestamp when this scene transitioned into MATCHED. Used by the local
   *  1Hz countdown to compute remaining seconds without depending on a
   *  server-emitted countdown stream (the server only broadcasts countdown
   *  ticks during the post-Ready `phase: 'countdown'` window). */
  private matchedAt: number | null = null;
  /** Browser-level `setInterval` handle for the matched-state 1Hz timer.
   *  Cleared on transition out of matched + on scene shutdown. */
  private matchedTimerId: ReturnType<typeof setInterval> | null = null;

  // ── UI handles (mutated by _renderForState) ──
  private headerText?: Phaser.GameObjects.Text;
  private statusText?: Phaser.GameObjects.Text;
  private joinBtn?: Phaser.GameObjects.Rectangle;
  private joinBtnText?: Phaser.GameObjects.Text;
  private leaveBtn?: Phaser.GameObjects.Rectangle;
  private leaveBtnText?: Phaser.GameObjects.Text;
  private readyBtn?: Phaser.GameObjects.Rectangle;
  private readyBtnText?: Phaser.GameObjects.Text;
  private countdownText?: Phaser.GameObjects.Text;

  /** Local "ready pressed" flag — true between pressReady and lobby-disbanded.
   *  Server is source of truth (lobby-state-update reflects both players'
   *  flags), but the local flag drives the button visual until the next update. */
  private localReady = false;

  // ──────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────

  /** Phaser init hook — picks up the save slot passed in from MenuScene's
   *  mode picker via `scene.start('<Lobby>', { saveSlot })`. */
  init(data?: { saveSlot?: number; fromInvite?: boolean; matchPayload?: MatchFoundPayload }): void {
    if (data?.saveSlot !== undefined) this.selectedSaveSlot = data.saveSlot;
    // Cross-mode scene transition: when we land here via an accepted invite
    // from a different mode, the match payload is forwarded so we can skip
    // the idle→queuing→matched state progression and land directly in matched.
    if (data?.fromInvite && data.matchPayload) {
      this.roomId = data.matchPayload.roomId;
      this.opponentId = data.matchPayload.opponentId;
      this.playerIndex = data.matchPayload.playerIndex;
      this.state = 'matched';
    }
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    this.cameras.main.setBackgroundColor('#0d0a0e');

    this._buildChrome();
    this._renderForState();
    this._registerSocketHandlers();

    // Flag this socket as "in the multiplayer menu" so friend invites can
    // reach us. Cleared on shutdown below. Idempotent — re-emitting on scene
    // transition between PvpLobbyScene ↔ CoopLobbyScene is safe.
    if (this.selectedSaveSlot !== undefined) {
      console.log('[mp-invite] enterMenu', { mode: this.mode, saveSlot: this.selectedSaveSlot });
      this.mpService.enterMenu(this.mode, this.selectedSaveSlot);
    } else {
      // Without an enter-menu emit, the server's `mpMenuMode` stays null and
      // every incoming invite for this user is rejected with target-not-in-menu.
      // Surface this to the console so it's visible during the next failed
      // invite repro — should never happen in normal flow (MenuScene always
      // passes saveSlot via init).
      console.warn('[mp-invite] entered lobby WITHOUT a saveSlot — invites to this user will be rejected as target-not-in-menu');
    }

    // Bind the scene-level invite-toast handler so incoming invites render
    // here while the lobby is active. Cleared on shutdown.
    const inviteHandler = (payload: { inviteId: string; fromUserId: string; fromName: string; mode: 'pvp' | 'coop'; expiresAt: number }): void => {
      InviteToast.getInstance().showInvite(this, payload);
    };
    this.mpService.bindInviteToast(inviteHandler);

    // Cleanup multiplayer listeners on scene shutdown so the next scene start
    // doesn't double-fire callbacks. MultiplayerService.cleanup() iterates the
    // listeners array and calls socket.off(event, fn) per registered pair —
    // it does NOT call socket.removeAllListeners(), so non-multiplayer
    // listeners (auth, game-session, version-check) survive scene transitions.
    this.events.once('shutdown', () => {
      this._stopMatchedCountdown();
      this.mpService.unbindInviteToast(inviteHandler);
      this.mpService.leaveMenu();
      this.mpService.cleanup();
    });

    // If the scene was entered already in matched (cross-mode invite — see
    // init), start the local countdown immediately so the user sees a live
    // timer instead of the frozen "60s" string.
    if (this.state === 'matched') {
      this.matchedAt = Date.now();
      this._startMatchedCountdown();
    }
  }

  // ──────────────────────────────────────────────
  // Public actions (called by buttons + tests)
  // ──────────────────────────────────────────────

  joinQueue(): void {
    if (this.state !== 'idle') return;
    const payload = this.getQueuePayload();
    if (!payload) {
      // Silent no-op was the bug: the user clicked Join Queue and nothing
      // happened, with no feedback. Surface the precondition so the user
      // knows what to fix (typical case for PvP: save level < PVP_MIN_STAGE).
      this._showLobbyToast(this.getQueueBlockedReason());
      return;
    }
    this.mpService.joinQueue(payload.mode, payload as never);
    this._transition('queuing');
  }

  /** Default blocked-reason message — concrete subclasses can override (e.g.
   *  PvP returns the min-stage requirement). Generic fallback is fine for
   *  Coop. Returned value is shown as a toast to the user. */
  protected getQueueBlockedReason(): string {
    if (this.selectedSaveSlot === undefined) return 'Sélectionne une sauvegarde avant de rejoindre la queue.';
    return "Cette sauvegarde n'est pas éligible pour la queue. Choisis-en une autre.";
  }

  /** Transient bottom-of-screen feedback for queue/invite errors. Auto-fades
   *  after ~2.5s. Mirrors the SocialHub.toast pattern. */
  protected _showLobbyToast(text: string): void {
    const t = this.add.text(GAME_W / 2, GAME_H - 28, text, {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#f0c040',
      backgroundColor: '#1a1008', padding: { x: 10, y: 5 },
    }).setOrigin(0.5).setDepth(50);
    this.tweens.add({ targets: t, alpha: 0, delay: 2200, duration: 320, onComplete: () => t.destroy() });
  }

  cancelQueue(): void {
    if (this.state !== 'queuing') return;
    this.mpService.leaveQueue();
    this._transition('idle');
  }

  pressReady(): void {
    if (this.state !== 'matched' && this.state !== 'launching') return;
    this.localReady = !this.localReady;
    this.mpService.toggleReady(this.roomId);
    this._renderForState();
  }

  leaveLobby(): void {
    if (this.state === 'idle') return;
    if (this.roomId) {
      // Tell the server we're leaving so it disbands the lobby, requeues the
      // opponent, and clears the 60s ready watchdog. Without this emit the
      // opponent is stranded in MATCHED/LAUNCHING until the watchdog fires.
      this.mpService.leaveLobby(this.roomId);
    }
    this._transition('idle');
  }

  /**
   * Return to the main menu from any state. Handles server-side cleanup per
   * state (cancels queue from QUEUING, disbands lobby from MATCHED/LAUNCHING)
   * before switching back to MenuScene.
   *
   * Exposed on the ← Menu button that is visible in all four states so the
   * player is never trapped in the lobby.
   */
  backToMenu(): void {
    if (this.state === 'queuing') {
      this.mpService.leaveQueue();
    } else if ((this.state === 'matched' || this.state === 'launching') && this.roomId) {
      this.mpService.leaveLobby(this.roomId);
    }
    this.scene.start('MenuScene', {});
  }

  // ──────────────────────────────────────────────
  // Socket event handlers
  // ──────────────────────────────────────────────

  private _registerSocketHandlers(): void {
    this.mpService.onMatchFound((payload: MatchFoundPayload) => {
      if (this.state !== 'queuing' && this.state !== 'idle') return;
      // Cross-mode invite: server emitted match-found for a different mode
      // than this lobby. Start the correct lobby and carry the payload so
      // the destination scene can land directly in matched state.
      if (payload.mode && payload.mode !== this.mode) {
        const sceneKey = payload.mode === 'pvp' ? 'PvpLobbyScene' : 'CoopLobbyScene';
        this.scene.start(sceneKey, {
          saveSlot: this.selectedSaveSlot,
          fromInvite: true,
          matchPayload: payload,
        });
        return;
      }
      this.roomId = payload.roomId;
      this.opponentId = payload.opponentId;
      this.playerIndex = payload.playerIndex;
      this.localReady = false;
      this._transition('matched');
      if (payload.opponentSave) this.renderOpponentCard(payload.opponentSave);
    });

    this.mpService.onLobbyUpdate((state: MultiplayerLobbyState) => {
      if (state.phase === 'countdown' && this.state === 'matched') {
        this._transition('launching');
      } else if (state.phase === 'hero_select' && this.state === 'launching') {
        // un-ready cancellation server-side
        this._transition('matched');
      }
      // Update countdown display if present
      if (state.phase === 'countdown' && this.state === 'launching' && state.countdown !== undefined) {
        this.countdownText?.setText(`Démarrage dans ${state.countdown}s...`);
      }
    });

    this.mpService.onLobbyDisbanded((payload: LobbyDisbandedPayload) => {
      this._handleLobbyDisbanded(payload.reason);
    });

    this.mpService.onGameStart((payload: GameStartPayload) => {
      // Transition to BattleScene — scene start triggers shutdown which cleans up listeners.
      this.scene.start('BattleScene', { multiplayerPayload: payload });
    });

    // Direct-invite reception is now bound at the service level (one
    // persistent listener) so a friend on MenuScene/HeroScene/BattleScene
    // also receives the toast. Each scene `bindInviteToast`s in its own
    // create() — done in this scene's setup block above this method.

    // Server rejects an accept (expired / already-matched) — surface the
    // error inside the matching toast so the user knows their click failed.
    this.mpService.onInviteError?.((payload) => {
      if (payload.inviteId) {
        InviteToast.getInstance().handleError(payload.inviteId, payload.message);
      }
    });
  }

  private _handleLobbyDisbanded(reason: LobbyDisbandedPayload['reason']): void {
    this.roomId = '';
    this.opponentId = '';
    this.localReady = false;
    switch (reason) {
      case 'ready-timeout-self':
      case 'both-timeout':
        // We are kicked from queue — back to IDLE, must re-click Join Queue
        this._transition('idle');
        break;
      case 'ready-timeout-other':
      case 'player-left':
        // Server requeues us — reflect QUEUING state. `player-left` covers both
        // the explicit Leave Lobby button and the disconnecting-event path
        // (runLobbyDisconnectSweep emits the same reason).
        this._transition('queuing');
        break;
      case 'no-heroes':
        // Defense-in-depth signal: the lobby reached countdown 0 with empty
        // hero arrays in Redis. Should never happen (the matchmaker
        // pre-resolves heroes for queue + invite paths). Show a clear toast
        // so the user isn't stuck with no feedback, then deterministically
        // transition back to IDLE so they can retry.
        this._showLobbyToast('Erreur: impossible de lancer la partie (héros manquants). Réessaye.');
        this._transition('idle');
        break;
    }
  }

  // ──────────────────────────────────────────────
  // State transitions + rendering
  // ──────────────────────────────────────────────

  protected _transition(next: MultiplayerLobbyState4): void {
    const wasMatched = this.state === 'matched';
    this.state = next;
    // Leaving matched/launching → tear down the opponent card.
    if (next === 'idle' || next === 'queuing') this.clearOpponentCard();
    // Matched-state local countdown lifecycle. Start when entering matched,
    // stop when leaving it. Server's READY watchdog runs authoritatively on
    // its own clock — this is purely a display companion so the user sees
    // remaining seconds.
    if (next === 'matched') {
      this.matchedAt = Date.now();
      this._startMatchedCountdown();
    } else if (wasMatched) {
      this._stopMatchedCountdown();
      this.matchedAt = null;
    }
    this._renderForState();
  }

  private _startMatchedCountdown(): void {
    this._stopMatchedCountdown();
    this.matchedTimerId = setInterval(() => {
      if (this.state !== 'matched' || this.matchedAt === null) {
        this._stopMatchedCountdown();
        return;
      }
      const remainingMs = MultiplayerLobbyBase.READY_TIMEOUT_MS - (Date.now() - this.matchedAt);
      const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
      this.statusText?.setText(`Match trouvé ! ${remaining}s pour valider Ready.`);
      if (remaining <= 0) {
        // Server's authoritative watchdog will fire shortly; stop the timer
        // so we don't keep painting "0s" for a long time.
        this._stopMatchedCountdown();
      }
    }, 1000);
  }

  private _stopMatchedCountdown(): void {
    if (this.matchedTimerId !== null) {
      clearInterval(this.matchedTimerId);
      this.matchedTimerId = null;
    }
  }

  private _renderForState(): void {
    // Update header (always visible)
    this.headerText?.setText(`${this.getModeLabel()}`);

    // Status text varies per state
    let status = '';
    switch (this.state) {
      case 'idle':       status = 'Sélectionne une sauvegarde puis rejoins la queue.'; break;
      case 'queuing':    status = 'Recherche d\'un adversaire...'; break;
      case 'matched':    status = `Match trouvé ! 60s pour valider Ready.`; break;
      case 'launching':  status = 'Les deux joueurs sont prêts.'; break;
    }
    this.statusText?.setText(status);

    // Toggle button visibility per state. Phaser objects support setAlpha for show/hide.
    const idleVisible = this.state === 'idle';
    const queuingVisible = this.state === 'queuing';
    const matchedOrLaunching = this.state === 'matched' || this.state === 'launching';
    const launching = this.state === 'launching';

    this._setVisible(this.joinBtn, idleVisible);
    this._setVisible(this.joinBtnText, idleVisible);
    this._setVisible(this.leaveBtn, !idleVisible);
    this._setVisible(this.leaveBtnText, !idleVisible);
    this._setVisible(this.readyBtn, matchedOrLaunching);
    this._setVisible(this.readyBtnText, matchedOrLaunching);
    this._setVisible(this.countdownText, launching);
    // Hit-testing: only the *button background rectangles* hold pointerdown
    // handlers. Toggle their interactivity in lock-step with visibility so
    // an invisible button can't capture a click in the overlap zone with a
    // visible neighbour. The text labels are decorative — never made
    // interactive — leave them out so we don't accidentally turn a label
    // into a no-op hit target that swallows the click.
    this._setInteractive(this.joinBtn, idleVisible);
    this._setInteractive(this.leaveBtn, !idleVisible);
    this._setInteractive(this.readyBtn, matchedOrLaunching);

    // Left-side save card: visible across IDLE/QUEUING/MATCHED/LAUNCHING —
    // the card stays on screen so the player can always see who they're
    // queuing as. Rebuilt only when we enter IDLE (save slot can't change
    // mid-lobby).
    if (idleVisible) {
      this.renderIdleSaveCard();
    }

    if (matchedOrLaunching) {
      this.readyBtnText?.setText(this.localReady ? '✓ Ready' : 'Ready');
      this.readyBtnText?.setColor(this.localReady ? '#7acc60' : '#c8973a');
      // Update leave button label to reflect context
      this.leaveBtnText?.setText('← Quitter');
    } else if (queuingVisible) {
      this.leaveBtnText?.setText('Annuler la queue');
    }
  }

  private _setVisible(obj: { setAlpha?: (a: number) => unknown } | undefined, visible: boolean): void {
    obj?.setAlpha?.(visible ? 1 : 0);
  }

  /** Toggle interactivity in lock-step with visibility — call ONLY on objects
   *  that already had `setInteractive` wired in `_buildChrome` (i.e. the
   *  Join/Leave/Ready button backgrounds). Calling `setInteractive` on a
   *  decorative text label would turn it into a hit target with no handler,
   *  silently swallowing clicks at higher depth than the underlying button. */
  private _setInteractive(
    obj: { setInteractive?: (cfg?: object) => unknown; disableInteractive?: () => unknown } | undefined,
    enabled: boolean,
  ): void {
    if (!obj) return;
    if (enabled) obj.setInteractive?.({ useHandCursor: true });
    else obj.disableInteractive?.();
  }

  /** Build / rebuild the left-side save card from the player's selected save
   *  slot. Called on every IDLE re-render. If `selectedSaveSlot` isn't set
   *  (scene entered without init data — defensive), renders a placeholder. */
  protected renderIdleSaveCard(): void {
    this.leftCard?.destroy();
    this.leftCard = undefined;

    const slot = this.selectedSaveSlot;
    const info = slot !== undefined ? GameStateManager.getInstance().getSaveSlotInfo(slot) : null;
    const accountName = AuthService.getInstance().getUser()?.username ?? 'You';

    this.leftCard = buildSaveSlotCard(this, {
      x: 160, y: GAME_H / 2,
      width: 228, height: 198,
      info,
      accentColor: this.getThemeColor(),
      accountName,
    });
  }

  /** Populate the right-side opponent card. Called from onMatchFound when
   *  `match-found { opponentSave }` arrives. Wired fully in Task 4; currently
   *  a stub that renders a "Waiting…" placeholder until the payload lands. */
  protected renderOpponentCard(info: {
    accountName: string;
    level: number;
    heroes: { heroKey?: string; heroClass: string; name: string; level: number }[];
  } | null): void {
    this.rightCard?.destroy();
    this.rightCard = undefined;

    if (!info) return;

    this.rightCard = buildSaveSlotCard(this, {
      x: GAME_W - 160, y: GAME_H / 2,
      width: 228, height: 198,
      info: {
        exists: true,
        level: info.level,
        heroLevel: info.level,
        heroes: info.heroes.length,
        faction: 'alliance',
        heroName: info.heroes[0]?.name ?? 'Opponent',
        heroClass: info.heroes[0]?.heroClass ?? 'warrior',
        setupComplete: true,
        heroData: info.heroes,
      },
      accentColor: this.getThemeColor(),
      accountName: info.accountName,
    });
  }

  /** Called on IDLE transition to clear the opponent card — the match is over
   *  or we left the lobby. */
  protected clearOpponentCard(): void {
    this.rightCard?.destroy();
    this.rightCard = undefined;
  }

  // ──────────────────────────────────────────────
  // UI scaffolding (chrome shared by all states)
  // ──────────────────────────────────────────────

  private _buildChrome(): void {
    const W = GAME_W;
    const H = GAME_H;
    const accent = this.getThemeColor();

    // ── Background (mode-specific zone art) ─────────────────────────────────
    // Mirrors MenuScene.ts:347-352. The texture-exists guard means scenes still
    // render on dev setups without processed environment assets.
    const bgKey = this.getBackgroundKey();
    if (this.textures.exists(bgKey)) {
      this.add.image(W / 2, H / 2, bgKey)
        .setDisplaySize(W, H)
        .setOrigin(0.5, 0.5)
        .setAlpha(1.0)
        .setDepth(-10);
    }
    // Dim overlay above the bg so text stays readable.
    this.add.rectangle(W / 2, H / 2, W, H, 0x0d0a0e, 0.55).setDepth(-9);

    // Header
    this.headerText = this.add.text(W / 2, 36, this.getModeLabel(), {
      fontSize: '22px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    // Subtle accent bar under header
    this.add.rectangle(W / 2, 64, 240, 2, accent, 0.85).setDepth(1);

    // Status banner (mid-low)
    this.statusText = this.add.text(W / 2, H - 110, '', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#b38a5a',
    }).setOrigin(0.5).setDepth(2);

    // Countdown display (LAUNCHING only)
    this.countdownText = this.add.text(W / 2, H - 145, 'Démarrage dans 10s...', {
      fontSize: '15px', fontFamily: 'Georgia, serif', color: '#7acc60', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);

    // Join Queue button (IDLE)
    this.joinBtn = this.add.rectangle(W / 2, H - 60, 220, 40, 0x1a1208, 0.95)
      .setStrokeStyle(2, accent).setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.joinQueue());
    this.joinBtnText = this.add.text(W / 2, H - 60, 'Join Queue', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(3);

    // Leave / Cancel button (QUEUING/MATCHED/LAUNCHING) — placed left
    this.leaveBtn = this.add.rectangle(W / 2 - 130, H - 60, 200, 36, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => {
        if (this.state === 'queuing') this.cancelQueue();
        else this.leaveLobby();
      });
    this.leaveBtnText = this.add.text(W / 2 - 130, H - 60, '← Quitter', {
      fontSize: '13px', fontFamily: 'Georgia, serif', color: '#a07840',
    }).setOrigin(0.5).setDepth(3);

    // Ready button (MATCHED/LAUNCHING)
    this.readyBtn = this.add.rectangle(W / 2 + 130, H - 60, 200, 40, 0x1a1208, 0.95)
      .setStrokeStyle(2, 0x7acc60).setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.pressReady());
    this.readyBtnText = this.add.text(W / 2 + 130, H - 60, 'Ready', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(3);

    // ── Menu button (top-left, always visible — bug 1 fix) ────────────────
    // Players in the lobby must always be able to return to MenuScene,
    // regardless of state. Separate from the Leave/Cancel button (which only
    // transitions in-scene): Menu = exit scene + server cleanup.
    this.add.rectangle(68, 36, 100, 28, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.backToMenu());
    this.add.text(68, 36, '← Menu', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(0.5).setDepth(3);

    // ── Social button (right of Menu) ─────────────────────────────────────
    // Opens SocialHub with mpMenuContext so friend rows show an Invite
    // button. Only visible in lobby scenes (MenuScene has its own Social
    // button in the nav grid).
    this.add.rectangle(164, 36, 100, 28, 0x0d0a08, 0.95)
      .setStrokeStyle(1, 0x5a3600).setDepth(2)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.openSocialHub());
    this.add.text(164, 36, '✦ Social', {
      fontSize: '12px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(0.5).setDepth(3);

    // Pending-request pip on the social button + friend-online toasts.
    // MenuScene/BattleScene attach these for their respective scenes; the MP
    // lobby scenes were missing them, so users in the lobby got no badge
    // updates and no friend-online popups. Both helpers self-clean on scene
    // shutdown (see SocialNotifications.ts cleanup hooks).
    attachPendingBadge(this, { x: 164 + 50 - 6, y: 36 - 14 });
    attachFriendOnlineToasts(this);
  }

  /** Open the SocialHub with `mpMenuContext` so the invite button surfaces
   *  for online friends. Closed explicitly by the hub's X button or by the
   *  right-click-to-whisper / invite fire-and-close path. Static import
   *  (see BattleScene.openSocial) — avoids stale chunk-hash fetch failures. */
  private openSocialHub(): void {
    new SocialHub(this, {
      mode: 'modal',
      sceneWidth: GAME_W,
      sceneHeight: GAME_H,
      mpMenuContext: { mode: this.mode },
      // No chat overlay in the lobby yet — whisper callback is a no-op.
      // When the feature ships in lobby scenes, wire this through.
      onWhisperRequested: () => {},
    });
  }
}
